import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Flex,
  Grid,
  Skeleton,
  Text,
} from '@backstage/ui';
import { useRouteRef } from '@backstage/core-plugin-api';
import { RiAddLine } from '@remixicon/react';
import pluralize from 'pluralize';
import {
  DocsHomeSearch,
  DocsHomeSearchError,
  DocsHomeSearchProvider,
} from './DocsHomeSearch';
import { CreateDocDialog } from './CreateDocDialog';
import { DocsSection } from './DocsSection';
import { ExampleQuote } from './ExampleQuote';
import { RecentDocs } from './RecentDocs';
import { DOC_TYPE_LIST } from '../types';
import type { DocTypeData } from '../types';
import { createTechdocsRootRef } from '../routes';
import { useDocTypeCounts } from '../hooks/useDocTypeCounts';
import { getCategoryPath } from '../utils/docsView';
import styles from './DocsHomeView.module.css';

export const DocsHomeView = () => {
  const [openCreateDocDialog, setOpenCreateDocDialog] = useState(false);

  return (
    <>
      <CreateDocDialog
        open={openCreateDocDialog}
        onClose={() => setOpenCreateDocDialog(false)}
      />

      <Flex direction="column" gap="10">
        <DocsHomeSearchProvider>
          <Flex direction="column" gap="4">
            <div className={styles.searchRow}>
              <div className={styles.search}>
                <DocsHomeSearch />
              </div>
              <Button
                variant="primary"
                onPress={() => setOpenCreateDocDialog(true)}
                iconStart={<RiAddLine />}
              >
                New document
              </Button>
            </div>
            <DocsHomeSearchError />
          </Flex>
        </DocsHomeSearchProvider>

        <CategoryCards />

        <RecentDocs emptyText="The docs that you open show here." />
      </Flex>
    </>
  );
};

function CategoryCards() {
  const getTechdocsRootPath = useRouteRef(createTechdocsRootRef);
  const { data: counts, isPending, error } = useDocTypeCounts();

  return (
    <DocsSection title="Categories">
      {error && (
        <Alert
          status="warning"
          icon
          title="Could not load the number of docs"
          description={error.message}
        />
      )}
      <Grid.Root columns={{ initial: '1', md: '3' }} gap="4">
        {DOC_TYPE_LIST.map(docType => (
          <CategoryCard
            key={docType.type}
            docType={docType}
            href={getCategoryPath(getTechdocsRootPath(), docType.type)}
            count={counts?.[docType.type] ?? 0}
            countState={getCountState(isPending, error)}
          />
        ))}
      </Grid.Root>
    </DocsSection>
  );
}

function CategoryCard({
  docType: { title, noun, icon: Icon, summary, example },
  href,
  count,
  countState,
}: {
  docType: DocTypeData;
  href: string;
  count: number;
  countState: 'pending' | 'error' | 'success';
}) {
  return (
    <Card href={href} label={title}>
      <CardBody className={styles.cardBody}>
        <Flex direction="column" gap="3" className={styles.cardContent}>
          <Flex align="center" gap="3">
            <Icon size={24} aria-hidden="true" />
            <Text as="h3" variant="body-large" weight="bold">
              {title}
            </Text>
          </Flex>
          <Flex direction="column" gap="3">
            <Text variant="body-medium">{toLeadIn(summary)}</Text>
            <ExampleQuote title={example.title} color="secondary" />
          </Flex>
          {/* Hide failed counts so that an unavailable count cannot appear as zero. */}
          {countState !== 'error' && (
            <div className={styles.count}>
              {countState === 'pending' ? (
                <Skeleton width={80} height={16} />
              ) : (
                <Text variant="body-small" color="secondary">
                  {pluralize(noun, count, true)}
                </Text>
              )}
            </div>
          )}
        </Flex>
      </CardBody>
    </Card>
  );
}

function toLeadIn(summary: string) {
  return `${summary.replace(/\.$/, '')}:`;
}

function getCountState(isPending: boolean, error: Error | null) {
  if (isPending) {
    return 'pending';
  }
  return error ? 'error' : 'success';
}
